import { useState } from 'react';
import { useMutation } from 'react-query';
import feathersClient from '../../apiClient';
import { TyTable, TyTableRow, TyTableSortHeader, NoDataTableRow, TyTablePagination } from '@tymbe/ty-components/table';
import { AdminPopup } from '@tymbe/schema/admin-popup.interface';
import Spinner from '../../components/Spinner';
import { TySearchBox } from '@tymbe/ty-components/inputs';
import useURLParamsHandler from '../../hooks/UrlParamsHandler/useURLParamsHandler';
import defaultPageSizeOptions from '../../components/Table/table.utils';
import { Form } from 'informed';
import Container from '../../containers';
import ModalShell from '../../components/modals/ModalShell';
import CreateAdminPopupPage from './CreateAdminPopup';
import EditAdminPopup from './EditAdminPopup';
import { PrimaryButton } from '@tymbe/ui-primitives/*';
import moment from 'moment';
import { BadgePill } from '@tymbe/ty-components/indicators';
import { listAdminPopupHeaders, useAdminPopupsQuery } from './listAdminPopupUtils';
import { SuccessAlert } from '../../components/alerts';
import AdminPopupModal from '../calendar/AdminPopupModal';

export default function ListAdminPopups() {
  const [{ allSearchParams }, setSearchParams] = useURLParamsHandler();
  const search = allSearchParams.search || '';

  const sort = allSearchParams.sort ?? { created_at: -1 };
  const currentPage = allSearchParams.page ?? 1;
  const pageSize = allSearchParams.pageSize ?? defaultPageSizeOptions[0].value;

  const pageNum = Number(currentPage);
  const pageSizeNum = Number(pageSize);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPopup, setSelectedPopup] = useState<AdminPopup | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [popupToDelete, setPopupToDelete] = useState<AdminPopup | null>(null);

  const { data, isLoading, refetch } = useAdminPopupsQuery({
    search,
    pageNum,
    pageSizeNum,
    sort,
  });

  const popups: AdminPopup[] = data?.data || [];
  const total = data?.total || 0;

  const deleteMutation = useMutation((id: number) => feathersClient.service('admin-popup').remove(id), {
    onSuccess: () => refetch(),
  });

  const handleSearch = (value?: string) => {
    setSearchParams({ search: value || '' });
  };

  const handleEdit = (popup: AdminPopup) => {
    setSelectedPopup(popup);
    setEditModalOpen(true);
  };

  const handleInfo = (popup: AdminPopup) => {
    setSelectedPopup(popup);
    setInfoModalOpen(true);
  };

  const handleDelete = (popup: AdminPopup) => {
    setPopupToDelete(popup);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (popupToDelete) {
      deleteMutation.mutate(popupToDelete.id);
      setDeleteModalOpen(false);
      setPopupToDelete(null);
      SuccessAlert('Popup smazán');
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setPopupToDelete(null);
  };

  const isPopupActive = (popup: AdminPopup) =>
    moment(popup.from).isBefore(moment()) && (moment(popup.to).isAfter(moment()) || popup.to === null);

  return (
    <Container>
      <div className="p-5">
        {/* header */}
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-heading-3-md font-semibold text-secondary-600">Seznam admin popupů</h1>
          <PrimaryButton type="button" color="primary" onClick={() => setCreateModalOpen(true)} disabled={isLoading}>
            Vytvořit popup
          </PrimaryButton>
        </div>

        {/* content */}
        <Form>
          <TySearchBox name="search" placeholder="Hledat popupy..." onSearch={handleSearch} />
          {isLoading ? (
            <Spinner show={true} absolute className="z-20 h-8 w-8 m-auto" />
          ) : (
            <>
              <div className="overflow-x-auto pt-4">
                <TyTable className="min-w-fit">
                  <tbody>
                    <TyTableRow className="border-x-0">
                      {listAdminPopupHeaders.map((col, idx) => (
                        <TyTableSortHeader
                          key={idx.toString()}
                          field={col.sortable ? col.value : undefined}
                          className="border-x-0"
                        >
                          {col.label}
                        </TyTableSortHeader>
                      ))}
                    </TyTableRow>
                    {popups.map((popup) => (
                      <TyTableRow key={popup.id}>
                        <td className="px-3 py-2">{popup.name}</td>
                        <td className="px-3 py-2 max-w-80 truncate">{popup.content}</td>
                        <td className="px-3 py-2">{popup.from ? moment(popup.from).format('DD.MM.YYYY') : '-'}</td>
                        <td className="px-3 py-2">{popup.to ? moment(popup.to).format('DD.MM.YYYY') : '-'}</td>
                        <td className="px-3 py-2">
                          {popup.created_at ? moment(popup.created_at).format('DD.MM.YYYY') : '-'}
                        </td>
                        <td className="px-3 py-2">
                          {isPopupActive(popup) ? (
                            <BadgePill variant="success">Aktivní</BadgePill>
                          ) : (
                            <BadgePill variant="default">Neaktivní</BadgePill>
                          )}
                        </td>
                        <td className="px-3 py-2 flex gap-2">
                          <BadgePill variant="info" className="cursor-pointer" onClick={() => handleInfo(popup)}>
                            Náhled
                          </BadgePill>
                          <BadgePill variant="warning" className="cursor-pointer" onClick={() => handleEdit(popup)}>
                            Upravit
                          </BadgePill>
                          <BadgePill variant="error" className="cursor-pointer" onClick={() => handleDelete(popup)}>
                            Smazat
                          </BadgePill>
                        </td>
                      </TyTableRow>
                    ))}
                    <NoDataTableRow visible={popups.length === 0} colSpan={listAdminPopupHeaders.length} />
                  </tbody>
                </TyTable>
              </div>
              <TyTablePagination className="!px-0" rowsCount={total} />
            </>
          )}
        </Form>

        {/* Edit popup modal */}
        <ModalShell showModal={editModalOpen} className="h-full w-[550px]" onClose={() => setEditModalOpen(false)}>
          {selectedPopup && (
            <EditAdminPopup
              initialValues={selectedPopup}
              onClose={() => {
                setEditModalOpen(false);
                refetch();
              }}
            />
          )}
        </ModalShell>

        {/* Create popup modal */}
        <ModalShell showModal={createModalOpen} className="h-full w-[550px]" onClose={() => setCreateModalOpen(false)}>
          <CreateAdminPopupPage
            onClose={() => {
              setCreateModalOpen(false);
              refetch();
            }}
          />
        </ModalShell>

        {/* Info popup modal */}
        <AdminPopupModal
          showPopup={infoModalOpen}
          popupData={selectedPopup}
          handleClosePopup={() => setInfoModalOpen(false)}
        />

        {/* Delete confirmation modal */}
        <ModalShell showModal={deleteModalOpen} onClose={cancelDelete}>
          <div className="p-6 flex flex-col items-center">
            <h2 className="text-heading-4-md mb-4 text-center">Opravdu chcete tento popup smazat?</h2>
            <div className="flex gap-4 mt-2">
              <button className="bg-error-500 text-white rounded px-4 py-2" onClick={confirmDelete}>
                Smazat
              </button>
              <button className="bg-secondary-500 text-white rounded px-4 py-2" onClick={cancelDelete}>
                Zrušit
              </button>
            </div>
          </div>
        </ModalShell>
      </div>
    </Container>
  );
}
